import { useEffect, useRef, useState } from "react";
import { Spinner } from "../components/Spinner";
import { TURNSTILE_SITE_KEY, loadTurnstile } from "../lib/turnstile";
import type { Partner } from "../partner/partners";
import { submitContact } from "./api";
import { CONTACT_TOPICS, MESSAGE_MAX, MESSAGE_MIN } from "./catalog";
import type { ContactOutcome, ContactTopic } from "./types";

/**
 * Whether the challenge can be answered at all.
 *
 * Three states rather than a boolean because the middle one is the whole
 * point: while the script is in flight there is nothing to show and nothing to
 * submit, and a send pressed in that window is refused by the Worker with
 * `challenge` — whose copy blames the visitor for leaving the form open. The
 * button waits instead.
 */
type ChallengeState = "loading" | "ready" | "unavailable";

/**
 * The anonymous contact form.
 *
 * It never touches the database. The `contact` schema is not in PostgREST's
 * exposed set and holds no grants for `anon`, so there is no route from a
 * browser to those tables at all — the form posts to the Cloudflare Worker,
 * which holds the only credential that can write.
 *
 * WHY THIS SHOWS REAL ERRORS, where `WaitlistForm` shows one screen whatever
 * happened: the waitlist's uniform response exists to stop the endpoint being
 * an oracle for checking whether a given address is already on the list. A
 * contact form holds no equivalent secret — the sender is describing their own
 * situation — so the honest answer is safe, and a false "sent" shown to
 * somebody reporting a problem is the worst outcome this page can produce.
 *
 * The one deception is the honeypot, and it deceives only something that filled
 * a field no person can see.
 */
export function ContactForm({ source, partner }: { source?: string; partner?: Partner | null }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  /**
   * An enquiry that arrived through a partner is a partnership enquiry, and
   * asking the visitor to say so from a list of six is asking them to restate
   * what their own link already said. The selector is replaced by the partner's
   * name, and the topic is fixed here rather than left to the field.
   *
   * It is still a value from the closed vocabulary in `types.ts`, so nothing
   * about partner mode widens what can reach the Worker's `TOPICS` map. Which
   * partner it was travels in `source`, which the Worker truncates to 64 chars
   * and the database length-checks — the name in this component is never sent.
   */
  const [topic, setTopic] = useState<ContactTopic>(partner ? "partnership" : "support");

  /**
   * `?topic=partnership` preselects the selector, so a "Partner with us" button
   * elsewhere on the site lands somebody on a form that already knows why they
   * came.
   *
   * Read in an effect rather than during render, for the same reason
   * `ContactPage` reads `?partner=` there: this page is pre-rendered at a path
   * with no query string, and reading `search` in the first render would make
   * the client's tree disagree with the server's.
   *
   * A closed lookup, never the raw value. Anything not in the catalogue is
   * ignored, so a crafted link cannot put an arbitrary string into the field
   * that the Worker uses to pick an email subject.
   */
  useEffect(() => {
    if (partner) return; // the partner decides the topic; the query string does not
    const asked = new URLSearchParams(window.location.search).get("topic");
    const match = CONTACT_TOPICS.find((t) => t.value === asked);
    if (match) setTopic(match.value);
  }, [partner]);

  // `partner` is resolved in an effect one tick after mount, so this component
  // can be mounted without one and acquire it. Without this the topic would
  // stay whatever it was at first render.
  useEffect(() => {
    if (partner) setTopic("partnership");
  }, [partner]);

  const [message, setMessage] = useState("");

  // Keyed, not a boolean: the ring belongs on the control that started the call,
  // and this form will grow a second button.
  const [busy, setBusy] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ContactOutcome | null>(null);

  const widget = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const [challenge, setChallenge] = useState<ChallengeState>(
    TURNSTILE_SITE_KEY ? "loading" : "unavailable",
  );

  /**
   * Render the widget once the script is up.
   *
   * THIS USED TO READ `window.turnstile` ONCE, ON MOUNT, and do nothing if it
   * was missing — which it always was, because nothing in the app ever loaded
   * the script. Even with the script added, a one-shot read on mount is a race
   * an async script loses about as often as it wins. `loadTurnstile()` resolves
   * when the API is genuinely there; see the header of lib/turnstile.ts.
   *
   * The action is pinned here and checked again in the Worker against
   * siteverify, so a token minted for the waitlist widget is refused here.
   */
  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then((api) => {
      if (cancelled) return;
      if (!api || !widget.current) {
        setChallenge("unavailable");
        return;
      }
      // The node survives StrictMode's mount/unmount/remount, so the flag on it
      // is what stops a second widget being rendered into the same div.
      if (!widget.current.dataset.rendered) {
        widgetId.current = api.render(widget.current, {
          sitekey: TURNSTILE_SITE_KEY!,
          action: "contact",
        });
        widget.current.dataset.rendered = "true";
      }
      setChallenge("ready");
    });
    return () => { cancelled = true; };
  }, []);

  /**
   * A Turnstile token is single-use, so any path that lets the person try again
   * has to mint a fresh one. The waitlist form has no retry and therefore no
   * reset; without this, the second attempt would fail for a reason that had
   * nothing to do with what the person corrected.
   */
  function resetChallenge() {
    const api = (window as Window & { turnstile?: { reset(id?: string): void } }).turnstile;
    if (api && widgetId.current !== null) api.reset(widgetId.current);
  }

  const tooShort = message.trim().length > 0 && message.trim().length < MESSAGE_MIN;
  const canSubmit =
    email.trim() !== "" &&
    message.trim().length >= MESSAGE_MIN &&
    busy === null &&
    challenge === "ready";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy("send");
    setOutcome(null);
    const params = new URLSearchParams(window.location.search);
    const api = (window as Window & { turnstile?: { getResponse(id?: string): string | undefined } })
      .turnstile;
    try {
      const result = await submitContact({
        name,
        email,
        topic,
        message,
        token: api?.getResponse(widgetId.current ?? undefined),
        source: source ?? params.get("utm_source"),
        campaign: params.get("utm_campaign"),
      });
      setOutcome(result);
      if (result.kind !== "sent") resetChallenge();
    } finally {
      setBusy(null);
    }
  }

  if (outcome?.kind === "sent") {
    return (
      <div className="card contact-sent" role="status">
        <p className="contact-sent-title">Almost there — check your email.</p>
        <p>
          We have sent a confirmation link to <strong>{email.trim().toLowerCase()}</strong>.
          Click it and your message reaches us.
        </p>
        {outcome.ref && (
          <p className="muted small">
            Your reference is <strong>{outcome.ref}</strong>.
          </p>
        )}
        <p className="muted small">
          {/* Said plainly, because it is the part people do not expect and the
              reason the form asks for nothing else. */}
          Nobody here reads the message until you confirm. That click is what proves the
          address is yours — it is why we do not ask you to create an account or type a code.
        </p>
      </div>
    );
  }

  const selected = CONTACT_TOPICS.find((t) => t.value === topic);

  return (
    <form className="card contact-form" onSubmit={submit} noValidate>
      {outcome && <FormProblem outcome={outcome} />}

      <div className="contact-field">
        <label htmlFor="contact-name">Your name</label>
        <input
          id="contact-name"
          value={name}
          maxLength={120}
          autoComplete="name"
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="contact-field">
        <label htmlFor="contact-email">
          Email address <span className="contact-required">required</span>
        </label>
        <input
          id="contact-email"
          type="email"
          required
          maxLength={254}
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-describedby="contact-email-help"
        />
        <p id="contact-email-help" className="muted small">
          We send a confirmation link here first, then our reply.
        </p>
      </div>

      {partner ? (
        /*
          Not a disabled <select> with one option, and not a read-only input:
          both look like controls, and a control that cannot be operated is a
          worse answer than plain text. This is a labelled value, which is what
          it is.
        */
        <div className="contact-field">
          <span className="contact-field-label" id="contact-partner-label">Partner name</span>
          <p className="contact-locked" aria-labelledby="contact-partner-label">
            <span className="contact-locked-value">{partner.name}</span>
            <span className="contact-locked-note">Introduced by Simino Social</span>
          </p>
          <p className="muted small">
            Your enquiry goes to us first and we introduce you — you will not be
            asked to repeat any of it.
          </p>
        </div>
      ) : (
        <div className="contact-field">
          <label htmlFor="contact-topic">What is it about?</label>
          <select
            id="contact-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value as ContactTopic)}
          >
            {CONTACT_TOPICS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {selected && <p className="muted small">{selected.hint}</p>}
        </div>
      )}

      <div className="contact-field">
        <label htmlFor="contact-message">
          Your message <span className="contact-required">required</span>
        </label>
        <textarea
          id="contact-message"
          required
          rows={7}
          maxLength={MESSAGE_MAX}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          aria-describedby="contact-message-help"
        />
        <p id="contact-message-help" className="muted small contact-counter">
          {tooShort
            ? `A few more words, please — at least ${MESSAGE_MIN} characters.`
            : partner
              ? "What you need made, where it will be used, and roughly when."
              : "What happened, what you expected, and the screen you were on."}
          <span>{message.trim().length} / {MESSAGE_MAX}</span>
        </p>
      </div>

      {/* Hidden from people, irresistible to bots. A filled one is answered
          exactly like a success, so nothing is learned by filling it. */}
      <input
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ display: "none" }}
        onChange={() => { /* never read; api.ts sends "" regardless */ }}
      />

      <div ref={widget} className="cf-turnstile" />

      {challenge === "unavailable" && (
        /*
          Said before the send is pressed rather than after. The Worker's refusal
          for a missing token is `challenge`, whose copy is about a form left
          open in a tab — true for an expired token, a lie here, and it sends
          somebody into a retry loop that cannot end.
        */
        <p className="err contact-problem" role="alert">
          The anti-spam check could not load, so the form cannot be sent from this
          browser. It is usually a privacy extension or a blocker holding back
          challenges.cloudflare.com. Allow it and reload, or email{" "}
          <a href="mailto:support@siminosocial.com">support@siminosocial.com</a> and
          we will pick it up there.
        </p>
      )}

      <button className="primary" type="submit" disabled={!canSubmit}>
        {busy === "send" ? (
          <Spinner label="Sending your message" />
        ) : challenge === "loading" ? (
          <Spinner label="Loading the anti-spam check" />
        ) : (
          "Send message"
        )}
      </button>

      <p className="muted small">
        We use what you send only to answer you. Unconfirmed messages are deleted after seven
        days. <a href="/privacy">Privacy</a>
      </p>
    </form>
  );
}

/**
 * One block, one honest sentence per outcome. Every one of these is recoverable
 * by the person reading it, which is the test a message has to pass to be worth
 * showing at all.
 */
function FormProblem({ outcome }: { outcome: ContactOutcome }) {
  const text = (() => {
    switch (outcome.kind) {
      case "invalid":
        return outcome.field === "email"
          ? "That email address does not look right. Check it and try again."
          : outcome.field === "message"
            ? "Your message is either too short or too long to send."
            : "Something in the form was not accepted. Check the fields and try again.";
      case "throttled":
        return "That is several messages in quick succession. Wait a minute and try again — " +
          "if it is urgent, email us directly at support@siminosocial.com.";
      case "challenge":
        return "The anti-spam check expired, which happens when a form sits open for a " +
          "while. It has been reset — press send again.";
      case "closed":
        return "The form is closed for the moment. Please email support@siminosocial.com " +
          "and we will pick it up there.";
      default:
        return "Your message did not send — nothing was saved. Please try again, or email " +
          "support@siminosocial.com directly so it does not get lost.";
    }
  })();

  return <p className="err contact-problem" role="alert">{text}</p>;
}
